<template>
  <bxs-bottom-sheets
    v-model="isShow"
    class="pdf-bottom-sheets"
    title="选择文档导出类型"
    height="middle"
    :use-b-scroll="false"
    :prevent-scroll-through="true"
  >
    <!-- 格式信息 -->
    <FormatSelection v-bind.sync="format" :miniprogram="miniprogram" />

    <!-- 内容信息 -->
    <ContentSelection v-model="content" :options="contentOptions" />

    <!-- 客户信息 -->
    <CustomerRemarks v-bind.sync="customer" />

    <div slot="footer" class="bottom-link">
      <a
        class="to-table-report"
        href="javascript:;"
        @click="handleDetermine"
      >确定</a>
    </div>
  </bxs-bottom-sheets>
</template>

<script>
import qs from 'qs'
import { get as _get, cloneDeep } from 'lodash'
import { APP_CHANNEL, PLANBOOK_API_HOSTNAME } from 'common/utils/variables'
import { PLANBOOK_PAGE_MAP } from '@/config/assets'
import { isWeCom, isWeEnterprise, isMiniBankInsurance } from 'base/config'
import { getExportOption } from 'api/index'
import {
  getUrlParam,
  buriedSubmit,
  parseTime,
  getLocalStorage,
  setLocalStorage
} from 'common/js/common'
import { BottomSheets, Icon } from 'bxs-ui-vue'
import FormatSelection from './components/FormatSelection'
import ContentSelection from './components/ContentSelection'
import CustomerRemarks from './components/CustomerRemarks'
import {
  REMAKRS_OPTIONS,
  STORAGE_KEY,
  STORAGE_NG_TIME,
  COVER_OPTIONS,
  CONTENT_OPTIONS
} from './components/config'

import { shareDirect } from '@/common/bridge'

export default {
  name: 'ExportSelection',
  components: {
    [Icon.name]: Icon,
    [BottomSheets.name]: BottomSheets,
    FormatSelection,
    ContentSelection,
    CustomerRemarks
  },
  props: {
    value: Boolean,
    miniprogram: Boolean,
    isFamily: Boolean, // 预留(家庭计划书)
    cardUuid: String // 预留(家庭计划书)
  },
  data () {
    this.uuid = getUrlParam(this.isFamily ? 'familyId' : 'uuid')
    return {
      // (api)导出配置
      exportOption: {},
      // 格式选择
      format: {
        typeval: 0,
        coverval: APP_CHANNEL === 'NUOYA' ? COVER_OPTIONS[0].value : 1
      },
      // 内容配置
      contentOptions: [],
      // 内容选择
      content: {
        introduceCompanyId: null,
        ideaId: null,
        diseaseShow: null,
        interestType: null,
        problemShow: null,
        cardType: 1
      },
      // 客户选择
      customer: {
        customerName: '',
        customerMessage: REMAKRS_OPTIONS[0]
      }
    }
  },
  computed: {
    isShow: {
      get () {
        return this.value
      },
      set (v) {
        this.$emit('input', v)
      }
    }
  },
  watch: {
    isShow (v) {
      const self = this
      const { uuid, exportOption } = self
      if (!v || (exportOption && Object.keys(exportOption).length)) return
      getExportOption({ uuid }).then(({ data: { success, data } } = { data: {} }) => {
        if (success) {
          self.exportOption = data
          self.contentOptions = self.formatOptions()
        }
      })
    }
  },
  created () {
    this.getStorage()
  },
  methods: {
    getStorage () {
      let exportValue = getLocalStorage(STORAGE_KEY)
      exportValue = JSON.parse(exportValue)
      if (exportValue) {
        this.content = exportValue.content || this.content 
        this.format = exportValue.format || this.format
        this.customer = exportValue.customer || this.customer
      }
    },
    setStorage () {
      const { format, content, customer } = this
      setLocalStorage(
        STORAGE_KEY,
        JSON.stringify({
          format,
          content: {
            ...content,
            introduceCompanyId: null
          },
          customer: {
            ...customer,
            customerName: ''
          }
        }),
        STORAGE_NG_TIME
      )
    },
    formatOptions () {
      const { exportOption, content, appPlatform } = this
      const { diseaseFlag, interestFlag, introduceCompanyId, introduceCompanyName } = exportOption
      let copyOptions = JSON.parse(JSON.stringify(CONTENT_OPTIONS)).map(item => {
        if (item.value === 'introduceShow') {
          const options = item.children && item.children.options ? item.children.options : []
          if (Array.isArray(options) && introduceCompanyId && introduceCompanyName) {
            if (!options.find(o => o.value === introduceCompanyId)) {
              options.splice(1, 0, {
                value: introduceCompanyId,
                label: introduceCompanyName
              })
            }
            // 当前用户公司与本地缓存公司（上一次选取）不一致
            // 设置为当前用户公司
            if (options.every(o => o.value !== content.introduceCompanyId)) {
              content.introduceCompanyId = introduceCompanyId
            }
            item.children.value = introduceCompanyId
          }
        }
        return item
      })
      copyOptions = copyOptions
        .filter(item => {
          if (!diseaseFlag && item.value === 'diseaseShow') {
            content.diseaseShow = null
            return false
          }
          if (!interestFlag && item.value === 'interestShow') {
            content.interestType = null
            return false
          }
          return true
        })
      if (['WECHAT_GAOKE'].includes(appPlatform)) {
        copyOptions = copyOptions.filter(item => !['introduceShow', 'cardType'].includes(item.value))
      }
      if (['H5_BXZ'].includes(appPlatform)) {
        copyOptions = copyOptions.filter(item => !['cardType'].includes(item.value))
      }
      return copyOptions
    },
    formatExportParams () {
      const {
        appPlatform,
        content,
        customer: { customerName, customerMessage },
        format: { coverval }
      } = this
      const params = {
        coverType: coverval,
        customerName: encodeURIComponent(customerName),
        customerMessage: encodeURIComponent(customerMessage)
      }
      if (isWeCom || isWeEnterprise || isMiniBankInsurance || ['H5_BXZ'].includes(appPlatform)) {
        content.cardType = -1
      }
      Object.keys(content).forEach(item => {
        const value = content[item]
        if (value !== null) {
          params[item] = value
        }
      })
      return params
    },
    async handleDetermine () {
      const { uuid, format, miniprogram, appPlatform } = this
      const isWord = format.typeval === 1
      const title = `${(document.title || '').replace(/\//g, '|')}${parseTime(new Date(), '{h}-{i}-{s}')}`
      const params = cloneDeep(this.formatExportParams())
      params.resultUuid = uuid
      const exportApi = `${PLANBOOK_API_HOSTNAME}/planBook/export/pb/export?`
      if (isWord) {
        params.word = true
        buriedSubmit('word')
      } else {
        buriedSubmit(['pdf_jianyue', 'pdf_shangwu', 'pdf_weiyi', 'pdf_shishang'][format.coverval - 1])
      }
      this.setStorage()
      // 企微（网页预览pdf）
      if (!isWord && (isWeCom || miniprogram)) {
        const toURL = `${exportApi}${qs.stringify(params)}`
        console.log('导出文档（企微）：', toURL)
        location.href = `${PLANBOOK_PAGE_MAP.pdfViewer}?file=${encodeURIComponent(toURL)}&title=${title}`
        return
      }

      const _saveInteractionSetting = this.$listeners.saveInteractionSetting
      if (_saveInteractionSetting) {
        const newResultUuid = await _saveInteractionSetting()
        if (newResultUuid) {
          params.resultUuid = newResultUuid
        }
      }

      const _params = cloneDeep(params)
      _params.customerMessage = decodeURIComponent(_params.customerMessage)
      _params.customerName = decodeURIComponent(_params.customerName)
      const toURL = `${exportApi}${qs.stringify(_params)}`

      if ('NUOYA' === APP_CHANNEL) {
        let imgUrl = COVER_OPTIONS.find(item => item.value === format.coverval && item.type === format.typeval)?.image
        shareDirect({
          title,
          shareUrl: toURL,
          imgUrl: imgUrl ?? null,
          type: isWord ? 3 : 2,
        })
        console.log('导出文档（NUOYA）：', toURL)
        return
      }
      if (appPlatform === 'WECHAT_GAOKE') {
        console.log('导出文档（高客）：', toURL)
        location.href = isWord ? toURL : `${PLANBOOK_PAGE_MAP.pdfViewer}?file=${encodeURIComponent(toURL)}&title=${title}&action=1`
        return 
      }

      const _HANDLE_FILE_DOWNLOAD_AND_SHARE = window.appBridge && window.appBridge.checkAppFeature('HANDLE_FILE_DOWNLOAD_AND_SHARE')
      if (_HANDLE_FILE_DOWNLOAD_AND_SHARE) {
        window.appBridge.handleFile({
          url: toURL,
          title,
          action: isWord ? 'downloadAndShare' : 'sharePdf'
        })
      } else {
        if(['H5_BXZ'].includes(appPlatform)) {
          window.open(toURL)
        } else {
          location.href = toURL
        }
      }
      console.log('导出文档：', toURL)
    }
  }
}
</script>